import { KeyValue } from '@angular/common';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  ColumnAligment,
  ColumnPipeTypes,
  IHowdenColumnConfig,
  ITableButtonConfig,
  ITableEventData,
  TableButtonColors
} from '@howdeniberia/core-front';
import { CLIENT_STATUS_VALUES } from '../../../core/constants/client-status.constants';
import { ClientStatus } from '../../../core/enums/client-status.enum';
import { IClientDto } from '../../../core/models/client.dto';
import { IClientsFilterDto } from '../../../core/models/clients-filter.dto';
import { IExecutiveDto } from '../../../core/models/executive.dto';
import { IGroupingCriteriaValueDTO } from '../../../core/models/grouping-criteria-value.dto';
import { IOfficeDto } from '../../../core/models/office.dto';

export class ClientSearchViewModel {
  private _filterForm: FormGroup;
  private _statusValues: KeyValue<ClientStatus, string>[] = CLIENT_STATUS_VALUES;
  private _offices: IOfficeDto[] = [];
  private _executives: IExecutiveDto[] = [];
  private _professions: IGroupingCriteriaValueDTO[] = [];
  private _searchRequest: IClientsFilterDto;
  private _sortedBy?: string;
  private _sortDirection?: string;
  private _length = 0;

  readonly C_NAME = 'name';
  readonly C_TAX_ID = 'taxId';
  readonly C_PHONE = 'phone';
  readonly C_EMAIL = 'email';
  readonly C_STATUS = 'status';
  readonly C_OFFICE_ID = 'officeId';
  readonly C_PROFESSION_ID = 'professionId';
  // Para los ejecutivos, tenemos que devolver el objeto de la lista, porque hay ejecutivos cuyo nombre
  // coincide con el id de otros y al seleccionar por id y descripción se cruzan los registros
  readonly C_EXECUTIVE = 'executive';

  constructor() {
    this._filterForm = new FormGroup({
      [this.C_NAME]: new FormControl('', []),
      [this.C_TAX_ID]: new FormControl('', []),
      [this.C_PHONE]: new FormControl('', []),
      [this.C_EMAIL]: new FormControl('', []),
      [this.C_STATUS]: new FormControl(ClientStatus.ActiveAndPotential, []),
      [this.C_OFFICE_ID]: new FormControl(null, []),
      [this.C_EXECUTIVE]: new FormControl(null, []),
      [this.C_PROFESSION_ID]: new FormControl(null, [])
    });

    this._searchRequest = {
      pageNumber: 0,
      pageSize: 10,
      sortedBy: this.sortedBy,
      sortDirection: this.sortDirection
    } as IClientsFilterDto;
  }

  data: IClientDto[] = [];
  columns: IHowdenColumnConfig[] = [
    {
      fieldName: 'noDues',
      fieldHeader: $localize`:@@app.client.search.noDues.column:Impagos`,
      canSort: true,
      pipeToApply: ColumnPipeTypes.CHECK,
      aligment: ColumnAligment.CENTER,
      width: '4%'
    },
    {
      fieldName: 'alias',
      fieldHeader: $localize`:@@app.client.search.alias.column:Alias`,
      canSort: true,
      width: '5%'
    },
    {
      fieldName: 'fullName',
      fieldHeader: $localize`:@@app.client.search.fullName.column:Nombre`,
      canSort: true,
      width: '20%'
    },
    {
      fieldName: 'status',
      fieldHeader: $localize`:@@app.client.search.status.column:Estado`,
      width: '5%'
    },
    {
      fieldName: 'creditAccount',
      fieldHeader: $localize`:@@app.client.search.creditAccount.column:Cta.Créd.`,
      canSort: true,
      pipeToApply: ColumnPipeTypes.CHECK,
      aligment: ColumnAligment.CENTER,
      width: '5%'
    },
    {
      fieldName: 'mobile',
      fieldHeader: $localize`:@@app.client.search.mobile.column:Teléfono`,
      canSort: true,
      width: '8%'
    },
    {
      fieldName: 'email',
      fieldHeader: $localize`:@@app.client.search.email.column:Email`,
      canSort: true,
      width: '10%'
    },
    {
      fieldName: 'office',
      fieldHeader: $localize`:@@app.client.search.office.column:Oficina`,
      canSort: true,
      width: '8%'
    },
    {
      fieldName: 'salesExecutive',
      fieldHeader: $localize`:@@app.client.search.salesExecutive.column:Ejecutivo`,
      canSort: true,
      width: '12%'
    },
    {
      fieldName: 'birth',
      fieldHeader: $localize`:@@app.client.search.birth.column:Nacimiento`,
      pipeToApply: ColumnPipeTypes.DATE,
      aligment: ColumnAligment.CENTER,
      canSort: true,
      width: '6%'
    },
    {
      fieldName: 'profession',
      fieldHeader: $localize`:@@app.client.search.profession.column:Profesión`,
      canSort: true,
      width: '12%'
    }
  ];

  buttons: ITableButtonConfig[] = [
    {
      name: 'extra-info',
      icon: 'info',
      tooltip: 'Información Adicional',
      color: TableButtonColors.INFO
    }
  ];

  getControl(controlName: string): AbstractControl<any, any> {
    return this._filterForm.get(controlName)!;
  }

  get offices(): Array<IOfficeDto> {
    return this._offices;
  }
  set offices(value: Array<IOfficeDto>) {
    this._offices = value;
  }

  get executives(): Array<IExecutiveDto> {
    return this._executives;
  }
  set executives(value: Array<IExecutiveDto>) {
    this._executives = value;
  }

  get professions(): Array<IGroupingCriteriaValueDTO> {
    return this._professions;
  }
  set professions(value: Array<IGroupingCriteriaValueDTO>) {
    this._professions = value;
  }

  get statusValues(): KeyValue<ClientStatus, string>[] {
    return this._statusValues;
  }

  get length(): number {
    return this._length;
  }
  set length(length: number) {
    this._length = length;
  }

  get sortedBy(): string | undefined {
    return this._sortedBy;
  }

  get sortDirection(): string | undefined {
    return this._sortDirection;
  }

  get searchRequest(): IClientsFilterDto {
    return this._searchRequest;
  }

  get filterForm(): FormGroup {
    return this._filterForm;
  }
  set filterForm(value: FormGroup) {
    this._filterForm = value;
  }

  get name(): string | null | undefined {
    return this._filterForm.get(this.C_NAME)?.value;
  }
  set name(value: string | null | undefined) {
    this._filterForm.get(this.C_NAME)?.setValue(value);
  }

  get taxId(): string | null | undefined {
    return this._filterForm.get(this.C_TAX_ID)?.value;
  }
  set taxId(value: string | null | undefined) {
    this._filterForm.get(this.C_TAX_ID)?.setValue(value);
  }

  get phone(): string | null | undefined {
    return this._filterForm.get(this.C_PHONE)?.value;
  }
  set phone(value: string | null | undefined) {
    this._filterForm.get(this.C_PHONE)?.setValue(value);
  }

  get email(): string | null | undefined {
    return this._filterForm.get(this.C_EMAIL)?.value;
  }
  set email(value: string | null | undefined) {
    this._filterForm.get(this.C_EMAIL)?.setValue(value);
  }

  get status(): ClientStatus | null | undefined {
    return this._filterForm.get(this.C_STATUS)?.value;
  }
  set status(value: ClientStatus | null | undefined) {
    this._filterForm.get(this.C_STATUS)?.setValue(value);
  }

  get officeId(): string | null | undefined {
    return this.getControl(this.C_OFFICE_ID)?.value;
  }
  set officeId(value: string | null | undefined) {
    this.getControl(this.C_OFFICE_ID)?.setValue(value);
  }

  get executive(): IExecutiveDto | null | undefined {
    return this.getControl(this.C_EXECUTIVE)?.value;
  }
  set executive(value: IExecutiveDto | null | undefined) {
    this.getControl(this.C_EXECUTIVE)?.setValue(value);
  }

  get professionId(): string | null | undefined {
    return this.getControl(this.C_PROFESSION_ID)?.value;
  }
  set professionId(value: string | null | undefined) {
    this.getControl(this.C_PROFESSION_ID)?.setValue(value);
  }

  get searchIsEnable(): boolean {
    if ((this.name && this.name.length > 2)
      || (this.taxId && this.taxId.length > 2)
      || (this.phone && this.phone.length > 2)
      || (this.email && this.email.length > 2)
      || this.professionId
      || this.officeId
      || this.executive
      && this.filterForm.valid
    ) {
      return true;
    }

    return false;
  }

  updateServerSideConfig(event: ITableEventData): void {
    this.searchRequest.pageNumber = event.pageIndex;
    this.searchRequest.pageSize = event.pageSize;
    this.searchRequest.sortedBy = event.fieldSorted;
    this.searchRequest.sortDirection = event.order;

    this.updateServerSideFilters();
  }

  updateServerSideFilters(): void {
    this.searchRequest.name = this.name;
    this.searchRequest.taxId = this.taxId;
    this.searchRequest.phone = this.phone;
    this.searchRequest.email = this.email;
    this.searchRequest.status = this.status === ClientStatus.All ? undefined : this.status;

    this.searchRequest.officeId = this.officeId;
    this.searchRequest.salesExecutiveId = this.executive?.executiveId;
    this.searchRequest.professionId = this.professionId?.toString();
  }

  cleanSearch() {
    this._filterForm.reset();
    this.data = [];
    this.status = ClientStatus.ActiveAndPotential;
  }
}
